<script setup>
import { ref, computed, onMounted } from 'vue'
import ArrowRight from './icons/ArrowRight.vue'

const { t } = useI18n()
const localePath = useLocalePath()
const { setTimeString } = useDateUtils()

const props = defineProps({
  collapseId: {
    type: String,
    default: '',
  },
  activeSlug: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  content: {
    type: String,
    default: '',
  },
  img: {
    type: Object,
    default: () => {},
  },
  actuality: {
    type: Object,
    default: () => {},
  },
  employees: {
    type: Array,
    default: () => [],
  },
  students: {
    type: Array,
    default: () => [],
  },
  departmentFields: {
    type: Object,
    default: () => {},
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
})

const toggle = ref(props.isOpen)

const when = computed(() => {
  return setTimeString(props.actuality.dateFrom, props.actuality.dateTo, t)
})

const canEmployeeGoToTheProfile = (employe) => {
  return (
    !!employe.resource.EmployeeFields.mobilePhone ||
    !!employe.resource.EmployeeFields.perex ||
    !!employe.resource.EmployeeFields.video ||
    !!employe.resource.EmployeeFields.web ||
    !!employe.resource.featuredImage ||
    !!employe.resource.ContentBlocks.flexibleFields.length
  )
}

onMounted(() => {
  toggle.value = props.isOpen
})
</script>

<template>
  <ClientOnly>
    <div class="collapseRow">
      <button
        tabindex="0"
        :aria-expanded="!toggle"
        class="collapseRow__link"
        @click.prevent="toggle = !toggle"
        @keyup.enter="toggle = !toggle"
      >
        <p class="collapseRow__text">
          <span
            v-if="title"
            class="collapseRow__title text_minor_3"
            :class="{
              'collapseRow__title--open': toggle,
            }"
          >
            {{ title }}
          </span>
          <span
            v-if="actuality && actuality.dateFrom"
            class="text_minor_3 is_c3 when"
          >
            {{ when }}
          </span>
        </p>
        <ArrowRight :class="{ arrow__rotate: toggle }" class="arrow" />
      </button>

      <Transition name="collapsed">
        <div v-show="toggle" class="collapsed">
          <div v-if="actuality" class="actuality">
            <div v-if="actuality.address" class="collapsed__location">
              <p class="text_minor_3 is_root" v-html="actuality.address"></p>
            </div>
            <img
              v-if="Object.keys(img).length > 0"
              :src="img.node.sourceUrl"
              :alt="img.node.altText || img.node.caption"
              class="collapsed__img"
            />
            <p
              v-if="actuality.perex"
              class="text_minor_3 is_c3"
              v-html="actuality.perex"
            />

            <NuxtLink
              :to="
                localePath({
                  name: 'notice-slug',
                  params: { slug: activeSlug },
                })
              "
              class="link text_minor_3"
            >
              {{ t('general.detail') }}
            </NuxtLink>
          </div>

          <!-- zaměstnanci -->
          <div
            v-for="(employe, index) in employees"
            :key="index"
            class="text_minor_3 contact"
          >
            <div>
              <p class="is_c2">
                <template v-if="employe.resource.EmployeeFields.prefix">
                  <span>{{ employe.resource.EmployeeFields.prefix }}</span>
                  <span>&nbsp;</span>
                </template>
                <span>{{ employe.resource.EmployeeFields.firstname }}</span>
                <span>&nbsp;</span>
                <span>
                  {{ employe.resource.title
                  }}<template v-if="employe.resource.EmployeeFields.suffix"
                    >, {{ employe.resource.EmployeeFields.suffix }}</template
                  >
                </span>
              </p>

              <p class="is_c3">
                {{ employe.function }}
              </p>
              <p v-if="employe.resource.EmployeeFields.phone">
                <a
                  :href="
                    'tel:' +
                    employe.resource.EmployeeFields.phone.replace(/,/g, ' ')
                  "
                >
                  {{ employe.resource.EmployeeFields.phone.replace(/,/g, ' ') }}
                </a>
              </p>
              <p>
                <a :href="'mailto:' + employe.resource.EmployeeFields.email">
                  {{ employe.resource.EmployeeFields.email }}
                </a>
              </p>
            </div>
            <div v-if="canEmployeeGoToTheProfile(employe)">
              <NuxtLink
                :to="
                  localePath({
                    name: 'employee-slug',
                    params: { slug: employe.resource.slug },
                  })
                "
                class="link text_minor_3"
              >
                {{ t('general.contacts.profile') }}
              </NuxtLink>
            </div>
          </div>
          <!-- studenti -->
          <div
            v-for="(student, index) in students"
            :key="index"
            class="text_minor_3 contact"
          >
            <div>
              <p class="is_c2">
                <template v-if="student.StudentFields.prefix">
                  <span>{{ student.StudentFields.prefix }}</span>
                  <span>&nbsp;</span>
                </template>
                <span>{{ student.StudentFields.firstname }}</span>
                <span>&nbsp;</span>
                <span>
                  {{ student.title }}
                  <template v-if="student.StudentFields.suffix">
                    ,&nbsp;{{ student.StudentFields.suffix }}
                  </template>
                </span>
              </p>
            </div>

            <div>
              <NuxtLink
                :to="
                  localePath({
                    name: 'student-slug',
                    params: { slug: student.slug },
                  })
                "
                class="link text_minor_3"
              >
                {{ t('general.contacts.profile') }}
              </NuxtLink>
            </div>
          </div>
          <!-- kontakt -->
          <div
            v-if="type === 'contact' && departmentFields"
            class="text_minor_3"
          >
            <div v-if="departmentFields.attachedAddress" class="address">
              <p class="is_c2">{{ departmentFields.attachedAddress.name }}</p>
              <p
                class="address__description"
                v-html="departmentFields.attachedAddress.description"
              ></p>
            </div>
            <p v-if="departmentFields.email">
              <span>{{ t('employee.email') }} </span>
              <a :href="'mailto:' + departmentFields.email">
                {{ departmentFields.email }}
              </a>
            </p>
            <p v-if="departmentFields.email2">
              <span>{{ t('employee.email') }} </span>
              <a :href="'mailto:' + departmentFields.email2">
                {{ departmentFields.email2 }}
              </a>
            </p>
            <p v-if="departmentFields.phone">
              <span>{{ t('employee.phone') }} </span>
              <a :href="'tel:' + departmentFields.phone.replace(/,/g, ' ')">
                {{ departmentFields.phone.replace(/,/g, ' ') }}
              </a>
            </p>
            <p v-if="departmentFields.phone2">
              <span>{{ t('employee.phone') }} </span>
              <a :href="'tel:' + departmentFields.phone2.replace(/,/g, ' ')">
                {{ departmentFields.phone2.replace(/,/g, ' ') }}
              </a>
            </p>
          </div>
          <!-- Flexible content -->
          <div v-if="type === 'flexible'" class="flexible">
            <p class="text_minor_3 flexible__content" v-html="content"></p>
          </div>
        </div>
      </Transition>
    </div>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.text_minor_3 {
  position: relative;
}
.collapseRow {
  border-bottom: 1px solid $color-5;
  border-top: 1px solid $color-5;
  padding: 20px 1px 20px 1px;
  margin: -1px 0 0 -1px;

  &:deep(ul) {
    margin: 16px;
    list-style: none;
  }

  &:deep(ul li::before) {
    content: '\2022';
    color: $color-4;
    font-weight: bold;
    display: inline-block;
    position: absolute;
    left: 0;
  }

  &:hover {
    .collapseRow__title {
      color: $color-root;
    }
  }

  &__link {
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    text-align: start;
    justify-content: space-between;
    padding-right: 5px;
    background-color: transparent;

    &:hover {
      cursor: pointer;
    }
  }
  &__text {
    display: flex;
    flex-flow: column nowrap;
    max-width: 75%;
  }
  .when {
    @include media-breakpoint-up(sm) {
      width: 21.528vw;
    }
  }
  &__title {
    color: $color-2;

    @include media-breakpoint-up(sm) {
      width: 21.528vw;
    }

    &--open {
      color: $color-root;
    }
  }

  .arrow {
    width: 15px;
    height: 15px;
    align-self: flex-start;
    -ms-transform: rotate(90deg); /* IE 9 */
    transform: rotate(90deg);
    transition: 0.5s;

    &__rotate {
      -ms-transform: rotate(-90deg); /* IE 9 */
      transform: rotate(-90deg);
    }
  }

  /* --- Transition Classes (název "collapsed") --- */

  /* Aktivní fáze animace */
  .collapsed-enter-active {
    overflow: hidden;
    transition:
      max-height 500ms ease-out,
      padding 500ms ease-out,
      opacity 500ms ease-out;
  }

  .collapsed-leave-active {
    overflow: hidden;
    transition:
      max-height 300ms ease-out,
      padding 300ms ease-out,
      opacity 300ms ease-out;
  }

  /* Počáteční stav ENTER a Koncový stav LEAVE (zavřeno) */
  .collapsed-enter-from,
  .collapsed-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  /* Koncový stav ENTER a Počáteční stav LEAVE (otevřeno) */
  .collapsed-enter-to,
  .collapsed-leave-from {
    opacity: 1;
    max-height: 1000px; /* Musí být dostatečné pro obsah */
  }

  /* --- Component Styling (zůstává BEM) --- */
  .collapsed {
    &__location {
      margin: 40px 0;
    }

    &__img {
      display: block;
      margin: 40px auto;
    }

    &__who {
      display: flex;
      flex-flow: row wrap;
      justify-content: center;
      align-items: center;
      margin-top: 20px;

      .link {
        padding: 0 5px;
      }
    }
  }

  .contact {
    border-bottom: 1px solid $color-5;
    padding: 12px 0;

    display: flex;
    justify-content: space-between;
    // align-items: flex-end;

    &:last-of-type {
      border: none;
    }
  }

  .address {
    padding: 12px 0;
    &__description {
      white-space: pre;
    }
  }

  .actuality {
    .link {
      width: fit-content;
      display: block;
      margin: 16px auto 0 auto;
    }
  }

  .flexible {
    &__content {
      padding: 12px 0;
    }
  }
}
</style>
